// Address autocomplete backed by LocationIQ — the app's canonical geocoder (same source as
// the legacy signup / marketplace / profile address fields), so results map to the DB's
// geo_* fields + the `City.get_from_geo_params` conventions and pass form validation.
// Replicates the fetch + `transformDisplay` + geo mapping of
// app/javascript/map/locationiq_autocomplete.js and form/components/address_autocomplete.js,
// WITHOUT the @tarekraafat autocomplete widget (the chatbot keeps its own bespoke dropdown).
// The public map key + brand country codes are client-exposed by design (see the legacy
// addresses/_input.html.slim); the island receives them via #signup-root data-* attributes.
import { ref, type InjectionKey } from 'vue'

// Canonical geo_* set (field names match address_autocomplete.js → the registration payload).
export interface AddressGeo {
  geo_route: string
  geo_postal_code: string
  geo_locality: string
  geo_streetnumber: string
  geo_administrative_area_level_1: string
  geo_administrative_area_level_2: string
  geo_country: string
  geo_latitude: number | null
  geo_longitude: number | null
}

export interface AddressSuggestion {
  label: string // display line: "route, postcode City"
  city: string // geo_locality (natural case) — used by the "N profils" bubble + villeLabel
  latitude: number | null
  longitude: number | null
  geo: AddressGeo // canonical set threaded into the registration payload
}

export interface AddressConfig {
  apiKey: string
  countryCodes: string // brand.searchable_country_codes, e.g. 'FR,BE,CH,AD'
  locale: string
}

// Provided by the island shell (SignupFlow) from #signup-root data-* attributes; AddressStep
// injects it so the composable stays config-driven (no globals). Falls back to a keyless config
// (no suggestions) when unprovided — e.g. isolated component tests / Storybook.
export const ADDRESS_CONFIG_KEY: InjectionKey<AddressConfig> = Symbol('signup-address-config')

interface LocationIqItem {
  lat?: string
  lon?: string
  type?: string
  class?: string
  address?: {
    name?: string
    house_number?: string
    road?: string
    city?: string
    postcode?: string
    suburb?: string
    state?: string
    country_code?: string
  }
}

const DEBOUNCE_MS = 500 // matches the legacy LocationIQ widget
const MIN_CHARS = 3
const TAG = 'highway,place:house,place:city,place:town,place:village'

// Mirrors transformDisplay(): derive route / postcode / city + a display label, with the
// city|village|town and locality|highway special cases. Unlike the legacy widget we keep
// LocationIQ's natural casing (no upper-casing) — the City lookup matches `name` OR
// `name_upcase` (case-insensitive), so display case has no bearing on DB/form resolution.
function toSuggestion(item: LocationIqItem): AddressSuggestion {
  const a = item.address ?? {}
  const houseNumber = a.house_number || ''
  let city = a.city || ''
  let route = `${houseNumber} ${a.road || ''}`.trim()
  const postcode = a.postcode || ''
  if (item.type === 'city' || item.type === 'village' || item.type === 'town') {
    city = a.name || ''
    route = ''
  } else if (item.type === 'locality' || item.class === 'highway') {
    city = a.city || ''
    route = a.name || ''
  }
  const label = `${route}${route ? ', ' : ''}${postcode} ${city}`.trim()
  const latitude = item.lat != null ? Number(item.lat) : null
  const longitude = item.lon != null ? Number(item.lon) : null
  return {
    label,
    city,
    latitude,
    longitude,
    geo: {
      geo_route: route,
      geo_postal_code: postcode,
      geo_locality: city,
      geo_streetnumber: houseNumber,
      geo_administrative_area_level_1: a.suburb || '',
      geo_administrative_area_level_2: a.state || '',
      geo_country: a.country_code || '',
      geo_latitude: latitude,
      geo_longitude: longitude,
    },
  }
}

// Map a captured address to the registration payload keys (RegistrationForm consumes
// geo_route/geo_postal_code/geo_locality + latitude/longitude → geo_latitude/geo_longitude).
export function geoToRegistrationPayload(geo: AddressGeo | null): Record<string, unknown> {
  if (!geo) return {}
  return {
    geo_route: geo.geo_route,
    geo_postal_code: geo.geo_postal_code,
    geo_locality: geo.geo_locality,
    latitude: geo.geo_latitude,
    longitude: geo.geo_longitude,
  }
}

function emptyGeo(locality: string): AddressGeo {
  return {
    geo_route: '',
    geo_postal_code: '',
    geo_locality: locality,
    geo_streetnumber: '',
    geo_administrative_area_level_1: '',
    geo_administrative_area_level_2: '',
    geo_country: '',
    geo_latitude: null,
    geo_longitude: null,
  }
}

export function useAddressAutocomplete(
  config: AddressConfig = { apiKey: '', countryCodes: 'FR', locale: 'fr' },
) {
  const query = ref('')
  const suggestions = ref<AddressSuggestion[]>([])
  const confirmed = ref<AddressSuggestion | null>(null) // the chosen address — gates the step's CTA
  let timer: ReturnType<typeof setTimeout> | null = null
  // LocationIQ responses can arrive out of order; only the latest request may write the list.
  let requestSeq = 0

  function clearTimer() {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  function dropInFlight(): void {
    requestSeq += 1
  }

  async function fetchSuggestions(): Promise<void> {
    const seq = ++requestSeq
    if (!config.apiKey) {
      suggestions.value = [] // no key wired (e.g. dev/test) → no suggestions, never throw
      return
    }
    const url =
      `https://api.locationiq.com/v1/autocomplete.php?key=${config.apiKey}` +
      `&limit=5&accept-language=${config.locale}&countrycodes=${config.countryCodes}` +
      `&tag=${TAG}&dedupe=1&q=${encodeURIComponent(query.value)}`
    let next: AddressSuggestion[] = []
    try {
      const res = await fetch(url)
      const data = (await res.json()) as LocationIqItem[]
      next = (Array.isArray(data) ? data : []).map(toSuggestion)
    } catch {
      next = [] // LocationIQ down / offline: no suggestions, never throw
    }
    if (seq !== requestSeq) return // superseded by a newer query, a pick or a reset
    suggestions.value = next
  }

  // Debounced input handler (bind to the address field's @input).
  function onInput(): void {
    clearTimer()
    if (query.value.length < MIN_CHARS) {
      dropInFlight()
      suggestions.value = []
      return
    }
    timer = setTimeout(fetchSuggestions, DEBOUNCE_MS)
  }

  // Select a suggestion → confirm it and close the dropdown.
  function pick(s: AddressSuggestion): void {
    dropInFlight()
    query.value = s.label
    confirmed.value = s
    suggestions.value = []
  }

  // Restore a previously-entered address (back-navigation to the step). With the geo set the
  // flow kept from the first pick, a re-confirmation carries the same coordinates and geo_*
  // values as that pick; without it only the label and locality survive. The availability
  // count was computed on the first pass (cached in useSignupFlow), so it's not refetched.
  function prefill(label: string, city?: string, geo?: AddressGeo | null): void {
    dropInFlight()
    query.value = label
    const locality = city || label
    confirmed.value = {
      label,
      city: locality,
      latitude: geo?.geo_latitude ?? null,
      longitude: geo?.geo_longitude ?? null,
      geo: geo ?? emptyGeo(locality),
    }
    suggestions.value = []
  }

  function reset(): void {
    clearTimer()
    dropInFlight()
    query.value = ''
    suggestions.value = []
    confirmed.value = null
  }

  return { query, suggestions, confirmed, onInput, pick, prefill, reset, DEBOUNCE_MS, MIN_CHARS }
}
